import { Base } from '../../abstract/base.model';
import { isRecord } from '../utils/is-record';
import { nullableNumber, nullableString } from '../utils/wire-value-utils';
import { SlaStatus } from './sla-status.model';
import { SupportTicketCategory } from './support-ticket-category.model';
import { SupportTicketPriority } from './support-ticket-priority.model';
import { SupportTicketStatus } from './support-ticket-status.model';
import { TicketChannel } from './ticket-channel.model';

/**
 * A customer support ticket, built from the hub's support-ticket JSON (`user-support-tickets-controller.ts`).
 * Synced both ways with Zoho Desk.
 *
 * Read-modify-write rule: writable fields the hub didn't send stay `undefined` (an explicit `null` stays
 * `null`). Display fallbacks belong in templates.
 */
export class SupportTicket extends Base {
  // Writable fields (SUPPORT_TICKET_WRITABLE)
  subject?: string | null;
  description?: string | null;
  category?: SupportTicketCategory | null;
  droneId?: string | null;

  // Read-only fields
  organizationId?: string | null;
  ticketNumber?: string | null;
  zohoDeskTicketNumber?: string | null;
  subCategory?: string | null;
  channel?: TicketChannel | null;
  /** Hub-controlled lifecycle; defaults to 'open' only when the hub omits it. */
  status: SupportTicketStatus;
  /** Set by support staff; defaults to 'medium' (the hub's create default) only when omitted. */
  priority: SupportTicketPriority;
  assignedToName?: string | null;
  assignedToEmail?: string | null;
  dueDate?: string | null;
  slaStatus?: SlaStatus | null;
  closedAt?: string | null;
  resolution?: string | null;
  responseTimeSeconds?: number | null;
  resolutionTimeSeconds?: number | null;
  zohoDeskWebUrl?: string | null;
  createdByProfileId?: string | null;

  // Display fields the hub joins in
  droneName?: string | null;
  droneSerialNumber?: string | null;
  createdByName?: string | null;

  constructor(data: unknown) {
    super(data);
    const source = isRecord(data) ? data : {};
    this.subject = nullableString(source['subject']);
    this.description = nullableString(source['description']);
    this.category = nullableString(source['category']) as SupportTicketCategory | null | undefined;
    this.droneId = nullableString(source['droneId']);
    this.organizationId = nullableString(source['organizationId']);
    this.ticketNumber = nullableString(source['ticketNumber']);
    this.zohoDeskTicketNumber = nullableString(source['zohoDeskTicketNumber']);
    this.subCategory = nullableString(source['subCategory']);
    this.channel = nullableString(source['channel']) as TicketChannel | null | undefined;
    this.status = (nullableString(source['status']) ?? 'open') as SupportTicketStatus;
    this.priority = (nullableString(source['priority']) ?? 'medium') as SupportTicketPriority;
    this.assignedToName = nullableString(source['assignedToName']);
    this.assignedToEmail = nullableString(source['assignedToEmail']);
    this.dueDate = nullableString(source['dueDate']);
    this.slaStatus = nullableString(source['slaStatus']) as SlaStatus | null | undefined;
    this.closedAt = nullableString(source['closedAt']);
    this.resolution = nullableString(source['resolution']);
    this.responseTimeSeconds = nullableNumber(source['responseTimeSeconds']);
    this.resolutionTimeSeconds = nullableNumber(source['resolutionTimeSeconds']);
    this.zohoDeskWebUrl = nullableString(source['zohoDeskWebUrl']);
    this.createdByProfileId = nullableString(source['createdByProfileId']);
    this.droneName = nullableString(source['droneName']);
    this.droneSerialNumber = nullableString(source['droneSerialNumber']);
    this.createdByName = nullableString(source['createdByName']);
  }
}
